import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/app-hooks';
import {
  clientTasksListQueryKey,
  completeClientTask,
  fetchClientTasks,
  reopenClientTask,
  selectClientTaskCounts,
  selectClientTasks,
  selectClientTasksFulfilledListQueryKey,
  selectClientTasksListStatus,
  selectClientTasksRequestedListQueryKey,
  selectClientTasksTotal,
  selectClientTaskMutationStatuses,
} from '../features/api/slices/client-tasks-slice';
import type {
  ClientTask,
  ClientTaskCounts,
  ClientTasksQuery,
  ClientTaskStatusFilter,
} from '../features/api/client-task-types';
import { useNewClientApi } from '../features/api/use-client-api';

const CLIENT_TASKS_PAGE_LIMIT = 25;

type ClientTasksPageSnapshot = {
  tasks: readonly ClientTask[];
  counts: ClientTaskCounts;
  total: number;
};

const totalForFilter = (
  counts: ClientTaskCounts,
  filter: ClientTaskStatusFilter,
) => (filter === 'all' ? counts.all : counts[filter]);

const useClientTasksTable = (programId?: string) => {
  const api = useNewClientApi();
  const dispatch = useAppDispatch();
  const { selectedLocale } = useLocale();
  const tasks = useAppSelector(selectClientTasks);
  const counts = useAppSelector(selectClientTaskCounts);
  const total = useAppSelector(selectClientTasksTotal);
  const listStatus = useAppSelector(selectClientTasksListStatus);
  const fulfilledListQueryKey = useAppSelector(
    selectClientTasksFulfilledListQueryKey,
  );
  const requestedListQueryKey = useAppSelector(
    selectClientTasksRequestedListQueryKey,
  );
  const mutationStatusByTaskId = useAppSelector(
    selectClientTaskMutationStatuses,
  );
  const [filter, setFilterState] = useState<ClientTaskStatusFilter>('all');
  const [page, setPageState] = useState(1);
  const pageCache = useRef(new Map<string, ClientTasksPageSnapshot>());
  const query = useMemo<ClientTasksQuery>(
    () => ({
      ...(programId ? { programId } : {}),
      status: filter,
      completedFilter: 'all',
      locale: selectedLocale,
      page,
      limit: CLIENT_TASKS_PAGE_LIMIT,
    }),
    [filter, page, programId, selectedLocale],
  );
  const queryKey = clientTasksListQueryKey(query);
  const hasLoadedQuery = fulfilledListQueryKey === queryKey;
  const isCurrentRequest = requestedListQueryKey === queryKey;
  const allTasksQueryKey = clientTasksListQueryKey({
    ...query,
    status: 'all',
  });
  const refresh = useCallback(
    () => dispatch(fetchClientTasks({ api, query })).unwrap(),
    [api, dispatch, query],
  );
  const setFilter = useCallback((nextFilter: ClientTaskStatusFilter) => {
    setFilterState(nextFilter);
    setPageState(1);
  }, []);
  const setPage = useCallback((nextPage: number) => {
    setPageState(Math.max(1, nextPage));
  }, []);
  const setCompleted = useCallback(
    async (taskId: string, completed: boolean) => {
      const result = await dispatch(
        completed
          ? completeClientTask({ api, taskId, locale: selectedLocale })
          : reopenClientTask({ api, taskId, locale: selectedLocale }),
      ).unwrap();
      if (page === 1) {
        void refresh().catch(() => undefined);
      } else {
        setPageState(1);
      }
      return result;
    },
    [api, dispatch, page, refresh, selectedLocale],
  );

  useEffect(() => {
    void refresh().catch(() => undefined);
  }, [refresh]);

  useEffect(() => {
    if (!hasLoadedQuery) return;

    pageCache.current.set(queryKey, { tasks, counts, total });
  }, [counts, hasLoadedQuery, queryKey, tasks, total]);

  const currentSnapshot = hasLoadedQuery
    ? { tasks, counts, total }
    : undefined;
  const cachedSnapshot = pageCache.current.get(queryKey);
  const allTasksSnapshot =
    filter === 'all' ? undefined : pageCache.current.get(allTasksQueryKey);
  const snapshot = currentSnapshot ?? cachedSnapshot ?? allTasksSnapshot;
  const usesAllTasksFallback =
    !currentSnapshot && !cachedSnapshot && !!allTasksSnapshot;
  const displayedTasks = usesAllTasksFallback
    ? allTasksSnapshot.tasks.filter((task) => task.status === filter)
    : (snapshot?.tasks ?? []);
  const displayedTotal = usesAllTasksFallback
    ? totalForFilter(allTasksSnapshot.counts, filter)
    : (snapshot?.total ?? 0);

  const status: 'idle' | 'pending' | 'succeeded' | 'failed' = snapshot
    ? 'succeeded'
    : !isCurrentRequest
      ? 'idle'
      : listStatus === 'failed'
        ? 'failed'
        : listStatus === 'pending'
          ? 'pending'
          : 'idle';

  return {
    tasks: displayedTasks,
    counts: snapshot?.counts ?? counts,
    filter,
    page,
    pageSize: CLIENT_TASKS_PAGE_LIMIT,
    total: displayedTotal,
    status,
    mutationStatusByTaskId,
    refresh,
    setFilter,
    setPage,
    setCompleted,
  };
};

export const useProgramTasks = (programId: string) =>
  useClientTasksTable(programId);

export const useDashboardTasks = () => useClientTasksTable();
